/**
 * Crucible #519 — sticky bail-out warning banner.
 * Crucible #796 — banner rewrite: count + severity + name list + drawer.
 *
 * Surfaces above the chat when the SKILL hit its per-feature / per-phase
 * budget mid-build and handed off with partial deliverables. The build-game
 * interview state file's `status` flips to `'built-partial'` on that path
 * (separate terminal from `'built'`, which means a fully-shipped game).
 *
 * **#796 improvements over the original copy:**
 * - Shows the count of skipped sub-phases prominently.
 * - Uses a severity-aware glyph: amber ⚠ (1-2), orange ⚠⚠ (3-5), red ❌ (6+).
 * - Shows the first 3 sub-phase names inline; remaining behind a drawer.
 * - Click-to-expand drawer lists all skipped sub-phases with their
 *   human-readable names (mirrors the `ActivityBanner` drawer pattern from
 *   PR #688 / C4 Issue F).
 * - Retains the `/build-game` recovery hint.
 *
 * **Producer**: the build-game SKILL in the hello-weekend repo writes
 * `'built-partial'` on its bail-out path. The per-transition `budgetSkipped`
 * flag is written by the SKILL via `append-phase-transition --budget-skipped`
 * and surfaced via the `phase-progress` IPC wire (`DerivedPhaseWire.transitions`).
 *
 * Renders nothing when status is missing or any value other than
 * `'built-partial'`. Listed all known statuses explicitly so a future
 * status addition (e.g. `'paused'`) doesn't accidentally fall through to
 * the banner.
 */

import { useId, useState } from 'react';
import type { BuildGameInterviewStatus } from '@volley/crucible-ipc-types/build-game-types';
import { subPhaseLabel } from '../workspace/subPhaseLabels';
export {
  SUB_PHASE_LABELS,
  knownSubPhaseLabel,
  subPhaseLabel,
} from '../workspace/subPhaseLabels';

// ---------------------------------------------------------------------------
// Sub-phase human-readable label map (Crucible #796)
// ---------------------------------------------------------------------------

// The label map now lives in `workspace/subPhaseLabels` so summary
// surfaces and detailed banners cannot drift. This component re-exports
// the helpers to preserve its existing test/import contract.

// ---------------------------------------------------------------------------
// Severity helpers (Crucible #796)
// ---------------------------------------------------------------------------

/**
 * Three severity tiers based on the count of skipped sub-phases.
 *
 * - `low`    1-2 skips — minor: one missing test or a small specialist.
 * - `medium` 3-5 skips — notable: several features may be missing.
 * - `high`   6+  skips — severe: substantially broken build.
 */
export type SkipSeverity = 'low' | 'medium' | 'high';

export function skipSeverity(count: number): SkipSeverity {
  if (count <= 2) return 'low';
  if (count <= 5) return 'medium';
  return 'high';
}

/**
 * Visual glyph for each severity tier.
 *
 * Exported for unit tests.
 */
export const SEVERITY_GLYPH: Record<SkipSeverity, string> = {
  low: '⚠',
  medium: '⚠⚠',
  high: '❌',
};

/**
 * Tailwind colour classes for the banner container and text, indexed by
 * severity.
 *
 * All class strings are static literals — no string interpolation — so
 * Tailwind's content scanner can detect them at build time (Bugbot #796
 * finding: dynamic `text-${severity}-100/85` constructions are invisible
 * to the scanner).
 */
const SEVERITY_STYLES: Record<
  SkipSeverity,
  {
    border: string;
    bg: string;
    drawerBg: string;
    text: string;
    mutedText: string;
    subText: string;
    codeBg: string;
    glyphClass: string;
    focusOutline: string;
    dismissHover: string;
    labelClass: string;
  }
> = {
  low: {
    border: 'border-amber-500/50',
    bg: 'bg-amber-500/15',
    drawerBg: 'bg-amber-500/10',
    text: 'text-amber-100',
    mutedText: 'text-amber-100/85',
    subText: 'text-amber-100/50',
    codeBg: 'bg-amber-500/25',
    glyphClass: 'text-amber-400',
    focusOutline: 'focus-visible:outline-amber-400/60',
    dismissHover: 'text-amber-100/70 hover:bg-amber-500/25 hover:text-amber-50',
    labelClass: 'text-amber-300/70',
  },
  medium: {
    border: 'border-orange-500/50',
    bg: 'bg-orange-500/15',
    drawerBg: 'bg-orange-500/10',
    text: 'text-orange-100',
    mutedText: 'text-orange-100/85',
    subText: 'text-orange-100/50',
    codeBg: 'bg-orange-500/25',
    glyphClass: 'text-orange-400',
    focusOutline: 'focus-visible:outline-orange-400/60',
    dismissHover: 'text-orange-100/70 hover:bg-orange-500/25 hover:text-orange-50',
    labelClass: 'text-orange-300/70',
  },
  high: {
    border: 'border-red-500/50',
    bg: 'bg-red-500/15',
    drawerBg: 'bg-red-500/10',
    text: 'text-red-100',
    mutedText: 'text-red-100/85',
    subText: 'text-red-100/50',
    codeBg: 'bg-red-500/25',
    glyphClass: 'text-red-400',
    focusOutline: 'focus-visible:outline-red-400/60',
    dismissHover: 'text-red-100/70 hover:bg-red-500/25 hover:text-red-50',
    labelClass: 'text-red-300/70',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Maximum number of sub-phase names shown inline (the rest collapse into the drawer). */
const INLINE_NAMES_LIMIT = 3;

export interface BuildPartialBannerProps {
  gameId: string;
  status: BuildGameInterviewStatus | undefined;
  /**
   * Crucible #796 — ordered list of sub-phase IDs (e.g. `'B.4b-features'`)
   * that were skipped due to budget exhaustion. Derived from
   * `phaseProgress.derived.transitions` filtered to `budgetSkipped === true`.
   *
   * Optional: when absent (or empty) the banner still renders for
   * `status === 'built-partial'` but uses a zero-count fallback ("some
   * sub-phases skipped") because the SKILL-emitted `budgetSkipped` markers
   * are a later addition. Passing an empty array is the same as omitting
   * the prop — the banner falls back to the legacy wording rather than
   * showing "0 sub-phases skipped".
   */
  skippedSubPhases?: readonly string[];
}

export function BuildPartialBanner({
  gameId,
  status,
  skippedSubPhases,
}: BuildPartialBannerProps): React.JSX.Element | null {
  // Dismissal state — per-mount, intentionally not persisted. A workspace
  // reopen brings the banner back; the cost is one click, but the
  // affordance is "make sure the user sees this state on every reopen
  // until they're ready to act on it" (preview the partial output,
  // resume the build with a fresh budget, or start over).
  const [dismissed, setDismissed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerPanelId = useId();

  if (dismissed) return null;
  if (status !== 'built-partial') return null;

  // Deduplicate within the component as defence-in-depth. The primary
  // dedup lives in Workspace.tsx (Array.from(new Set(…))), but callers
  // that pass duplicates would otherwise produce React key warnings and
  // potentially silently drop or mis-reconcile drawer list items.
  const skipped = Array.from(new Set(skippedSubPhases ?? []));
  const skipCount = skipped.length;

  // Severity-aware styling
  const severity = skipSeverity(skipCount);
  const styles = SEVERITY_STYLES[severity];
  const glyph = SEVERITY_GLYPH[severity];

  // Inline vs drawer split
  const inlineNames = skipped.slice(0, INLINE_NAMES_LIMIT);
  const overflowCount = skipCount - inlineNames.length;
  const hasDrawer = skipped.length > 0;

  // Count label — fall back when no markers are available
  const countLabel =
    skipCount === 0
      ? 'Built with budget skips.'
      : skipCount === 1
        ? '1 sub-phase skipped.'
        : `${skipCount} sub-phases skipped.`;

  return (
    <div
      data-testid="build-partial-banner"
      data-game-id={gameId}
      data-skip-count={skipCount}
      data-severity={severity}
    >
      {/* Main banner row */}
      <div
        className={`flex flex-none items-center justify-between border-b ${styles.border} ${styles.bg} py-2.5 pl-20 pr-6 text-sm ${styles.text}`}
        role="status"
        aria-live="polite"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* Severity glyph */}
          <span
            aria-hidden="true"
            className={`flex-none text-base ${styles.glyphClass}`}
            data-testid="build-partial-banner-glyph"
          >
            {glyph}
          </span>

          <div className="min-w-0">
            {/* Count headline */}
            <span className="font-medium" data-testid="build-partial-banner-count">
              {countLabel}
            </span>{' '}

            {skipCount > 0 ? (
              <>
                {/* Inline name list */}
                <span
                  className={styles.mutedText}
                  data-testid="build-partial-banner-names"
                >
                  {inlineNames.map((id) => subPhaseLabel(id)).join(', ')}
                  {overflowCount > 0 && (
                    <>
                      {' '}
                      <button
                        type="button"
                        onClick={() => setDrawerOpen((o) => !o)}
                        aria-expanded={drawerOpen}
                        aria-controls={drawerPanelId}
                        aria-label={
                          drawerOpen
                            ? `Hide all ${skipCount} skipped sub-phases`
                            : `Show ${overflowCount} more skipped sub-phases`
                        }
                        className={`underline underline-offset-2 hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2 ${styles.focusOutline}`}
                        data-testid="build-partial-banner-overflow-btn"
                      >
                        +{overflowCount} more
                      </button>
                    </>
                  )}
                  {'  '}
                </span>
              </>
            ) : null}

            {/* Recovery hint — always shown */}
            <span className={styles.mutedText}>
              Continue the build with{' '}
              <code className={`rounded ${styles.codeBg} px-1`}>/build-game</code>{' '}
              to pick up where the bail-out fired.
            </span>
          </div>
        </div>

        <div className="ml-4 flex flex-none items-center gap-2">
          {/* Drawer toggle — only shown when there are skipped phases to list */}
          {hasDrawer && (
            <button
              type="button"
              onClick={() => setDrawerOpen((o) => !o)}
              aria-expanded={drawerOpen}
              aria-controls={drawerPanelId}
              aria-label={
                drawerOpen
                  ? 'Hide skipped sub-phases'
                  : 'Show skipped sub-phases'
              }
              data-testid="build-partial-banner-drawer-toggle"
              className={`rounded-md px-2 py-1 text-xs ${styles.dismissHover} focus-visible:outline-2 focus-visible:outline-offset-2 ${styles.focusOutline}`}
            >
              {drawerOpen ? '▴ Hide details' : '▾ Details'}
            </button>
          )}

          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid="build-partial-banner-dismiss"
            className={`rounded-md px-2 py-1 text-xs ${styles.dismissHover}`}
            title="Dismiss this warning for the current session"
          >
            Dismiss
          </button>
        </div>
      </div>

      {/* Drawer — full skipped sub-phase list */}
      {drawerOpen && hasDrawer && (
        <div
          id={drawerPanelId}
          role="region"
          aria-label="Skipped sub-phases"
          data-testid="build-partial-banner-drawer"
          className={`border-b ${styles.border} ${styles.drawerBg} px-20 py-3`}
        >
          <p
            className={`mb-2 text-xs font-medium uppercase tracking-wide ${styles.labelClass}`}
          >
            Sub-phases skipped due to budget ceiling
          </p>
          <ul
            className="space-y-1"
            data-testid="build-partial-banner-drawer-list"
          >
            {skipped.map((id) => (
              <li
                key={id}
                className={`flex items-center gap-2 text-sm ${styles.text}`}
                data-subphase-id={id}
              >
                <span
                  aria-hidden="true"
                  className={`text-xs ${styles.glyphClass}`}
                >
                  {SEVERITY_GLYPH.low}
                </span>
                <span className="font-medium">{subPhaseLabel(id)}</span>
                <span className={`text-xs ${styles.subText}`}>
                  {id}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
